import React from "react";
import { useTenant } from "@hooks";
import locale from "@bonliva-crm/locale";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  TableCell as TableCellBase,
  TableHead,
  TableBody,
  Table,
  TableRow,
  Typography,
  Paper
} from "@material-ui/core";
import {
  Add,
  Edit,
  FileCopy,
  Remove,
  Restore,
  DragHandle,
  Info
} from "@material-ui/icons";
import {
  ContractsPriceTemplateType,
  PayPeriod,
  PayUnit,
  PriceListWorkType
} from "@graphql";
import { PriceListDay } from "@bonliva-crm/shared";
import {
  recordType,
  sortItem
} from "@config_editor/ContractsPriceTemplates/components/PriceRecordAltStore";
import SelectCommon, { Option } from "@common/Select";
import {
  dailyRecordOpt,
  startDayOpt,
  endDayOpt,
  endDayOptFullPeriod,
  startDayOptFullPeriod
} from "@config_editor/ContractsPriceTemplates/utils";
import { Text } from "../../../common/formx/Text";
import {
  getCustomPrice,
  getFixedPrice,
  getPrice
} from "@config_editor/ContractsPriceTemplates/Search/components/PriceRecordOutput";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { ThemeType } from "@theme";
import { getValidationIcon } from "@config_editor/ContractsPriceTemplates/validation/validationIcon";
import { List, arrayMove, arrayRemove } from "react-movable";
import { IItemProps } from "react-movable/lib/types";
import { useObserver } from "mobx-react";
import { Select } from "../../../common/formx/Select";
import { ValidateCustomerContractOptionResult } from "@bonliva-crm/timereport-api-client-js";
import { WithTooltip } from "@common";
import { store } from "@store";

const TableCell = (props) => (
  <TableCellBase
    size="small"
    style={{ ...props.style, padding: 2, verticalAlign: "middle" }}
    {...props}
  />
);

type Props = {
  disabled: boolean;
  formStore: any;
  activeWorkTypes: PriceListWorkType[];
  isCustom: boolean[];
  toggleDayTypeDialog: (workType: PriceListWorkType) => void;
  handleShowCustomInput: (
    e: React.SyntheticEvent<EventTarget>,
    i: string,
    name: string
  ) => void;
  handleSortRecords: (workType: PriceListWorkType, items: sortItem[]) => void;
  handleDuplicate: (r: recordType) => void;
  handleDelete: (
    workType: PriceListWorkType,
    items: sortItem[],
    record: recordType
  ) => void;
  workType: PriceListWorkType;
  records: recordType[];
  items: sortItem[];
  type?: ContractsPriceTemplateType;
  validationResult?: ValidateCustomerContractOptionResult[];
  isNationalProcurementPriceStrategy?: boolean;
  showArticleNumberForOtherSpeciality?: boolean;
};

type RowProps = Omit<
  Props,
  | "workType"
  | "toggleDayTypeDialog"
  | "records"
  | "activeWorkTypes"
  | "items"
  | "handleSortRecords"
  | "handleDelete"
  | "handleDuplicate"
> & {
  record: recordType;
  dragProps: IItemProps;
  handleDuplicate: () => void;
  handleDelete: () => void;
  templateType: ContractsPriceTemplateType;
  showInputDays?: boolean;
  startDayOptions?: Option[];
  endDayOptions?: Option[];
  showInputPeriod?: boolean;
  isNationalProcurementPriceStrategy?: boolean;
  showArticleNumberForOtherSpeciality?: boolean;
};

export function RecordsByWorkType({
  formStore,
  disabled,
  type,
  workType,
  records,
  items,
  handleSortRecords,
  isCustom,
  activeWorkTypes,
  toggleDayTypeDialog,
  handleShowCustomInput,
  handleDuplicate,
  handleDelete,
  validationResult,
  isNationalProcurementPriceStrategy,
  showArticleNumberForOtherSpeciality = false
}: Props) {
  return useObserver(() => {
    return (
      activeWorkTypes.includes(workType) && (
        <Paper key={workType} style={{ marginBottom: 10 }}>
          <Typography
            variant={"h6"}
            style={{
              padding: "0 16px",
              border: Object.keys(formStore.errors).includes("SalaryRecords")
                ? "1px solid red"
                : "initial"
            }}
          >
            {locale.contractsPrice.priceListWorkType[workType]}
            <IconButton
              color={"secondary"}
              aria-label="add"
              onClick={() => toggleDayTypeDialog(workType)}
            >
              <Add fontSize="small" />
            </IconButton>
          </Typography>
          {items?.length > 0 && (
            <List<sortItem>
              // beforeDrag={({ elements, index }) => {
              //   const cells = Array.from(elements[index].children);
              //   const widths = cells.map(cell => window.getComputedStyle(cell).width);
              //   setWidths(widths);
              // }}
              values={items}
              onChange={({ oldIndex, newIndex }) => {
                handleSortRecords(workType, arrayMove(items, oldIndex, newIndex));
              }}
              renderList={({ children, props }) => (
                <Table cellSpacing={2}>
                  <TableHead>
                    <TableRow
                      style={{
                        background: "rgba(81, 163, 154, 0.1)",
                        textAlign: "center"
                      }}
                    >
                      <TableCell style={{ width: 100 }}>
                        {locale.customerContractEdit.articleNumber}
                      </TableCell>
                      {showArticleNumberForOtherSpeciality && (
                        <TableCell style={{ width: 100 }}>
                          {locale.customerContractEdit.articleNumberForOtherSpeciality}
                        </TableCell>
                      )}
                      <TableCell style={{ width: 140 }}>
                        {locale.customerContractEdit.paidPer}
                      </TableCell>
                      <TableCell style={{ width: 120 }} align={"center"}>
                        {locale.Period2}
                      </TableCell>
                      <TableCell style={{ minWidth: 100 }}>{locale.From}</TableCell>
                      {/*from*/}
                      <TableCell style={{ width: 50 }} />
                      {/* - dash*/}
                      <TableCell style={{ width: 10, padding: 0 }} align={"center"} />
                      <TableCell style={{ minWidth: 100 }}>{locale.To}</TableCell>
                      {/*to*/}
                      <TableCell style={{ width: 50 }} />
                      <TableCell style={{ width: 70 }}>
                        {locale.PriceMultiplier}{" "}
                      </TableCell>
                      <TableCell style={{ width: 70 }}>{locale.FixedPrice}</TableCell>

                      {isNationalProcurementPriceStrategy &&
                        type === ContractsPriceTemplateType.ConsultantContract && (
                          <>
                            <TableCell style={{ width: 70 }}>
                              {locale.FixedPrice + " <7,5"}
                            </TableCell>
                            <TableCell style={{ width: 70 }}>
                              {locale.FixedPrice + " >7,5"}
                            </TableCell>
                          </>
                        )}

                      <TableCell style={{ width: 70 }} align="right">
                        {locale.Total}
                      </TableCell>

                      {isNationalProcurementPriceStrategy &&
                        type === ContractsPriceTemplateType.CustomerContract && (
                          <TableCell>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              {locale.basicPrice}{" "}
                              <WithTooltip
                                placement={"top"}
                                title={locale.basicPriceCheckboxInfo}
                              >
                                <Info fontSize={"small"} color="disabled" />
                              </WithTooltip>
                            </div>
                          </TableCell>
                        )}

                      {isNationalProcurementPriceStrategy &&
                        type === ContractsPriceTemplateType.ConsultantContract && (
                          <>
                            <TableCell style={{ width: 70 }}>
                              {locale.Total + " <7,5"}
                            </TableCell>
                            <TableCell style={{ width: 70 }}>
                              {locale.Total + " >7,5"}
                            </TableCell>
                          </>
                        )}

                      <TableCell style={{ width: 94 }} />
                    </TableRow>
                  </TableHead>

                  <TableBody {...props}>{children}</TableBody>
                </Table>
              )}
              renderItem={({ value, props, index }) => {
                const record = records.find((x) => x._id === value._id);
                if (!record) {
                  return null;
                }
                return (
                  <Row
                    key={value._id}
                    dragProps={props}
                    record={record}
                    formStore={formStore}
                    validationResult={validationResult}
                    disabled={disabled}
                    templateType={type}
                    isCustom={isCustom}
                    handleDuplicate={() => handleDuplicate(record)}
                    handleDelete={() =>
                      handleDelete(
                        workType,
                        typeof index !== "undefined" ? arrayRemove(items, index) : items,
                        record
                      )
                    }
                    handleShowCustomInput={handleShowCustomInput}
                    isNationalProcurementPriceStrategy={
                      isNationalProcurementPriceStrategy
                    }
                    showArticleNumberForOtherSpeciality={
                      showArticleNumberForOtherSpeciality
                    }
                  />
                );
              }}
            />
          )}
        </Paper>
      )
    );
  });
}

const Row = ({ ...props }: RowProps) => {
  if (props.record.isFreeInput) {
    return (
      <RowBasic
        startDayOptions={startDayOpt}
        endDayOptions={endDayOpt}
        showInputDays
        showInputPeriod
        {...props}
      />
    );
  } else if (props.record.period === PayPeriod.Daily) {
    switch (props.record.startDay) {
      case PriceListDay.BusinessDay:
      case PriceListDay.SqueezedDay:
      case PriceListDay.Weekends:
      case PriceListDay.Holiday:
      case PriceListDay.MajorHoliday:
      case PriceListDay.AnyTime:
        return <RowBasic {...props} />;
      case null:
        // locale.SpecificDaily
        return <RowBasic showInputDays={true} {...props} />;
      default:
        return (
          <RowBasic
            startDayOptions={startDayOpt}
            endDayOptions={endDayOpt}
            showInputDays
            showInputPeriod
            {...props}
          />
        );
    }
  } else if (props.record.period === PayPeriod.FullPeriod) {
    return <RowFullPeriod {...props} />;
  }

  return (
    <RowBasic
      startDayOptions={startDayOpt}
      endDayOptions={endDayOpt}
      showInputDays
      showInputPeriod
      {...props}
    />
  );
};

const RowBasic = ({
  record,
  showInputDays = false,
  dragProps,
  disabled,
  formStore,
  templateType,
  isCustom,
  handleShowCustomInput,
  handleDuplicate,
  handleDelete,
  validationResult,
  showInputPeriod,
  startDayOptions,
  endDayOptions,
  isNationalProcurementPriceStrategy,
  showArticleNumberForOtherSpeciality
}: RowProps) => {
  const theme = useTheme();
  const { tenant } = useTenant();
  const classes = useStyles(theme);
  const validationItem = validationResult.find((v) => v.contractOptionId === record._id);
  const validationIcon = getValidationIcon(validationItem);
  return (
    <TableRow
      {...dragProps}
      key={record._id} // don't remove this to avoid holy crap
      className={classes.row}
    >
      <TableCell>
        <Text name={`${record.name}.articleNumber`} disabled={disabled} />
      </TableCell>
      {showArticleNumberForOtherSpeciality && (
        <TableCell>
          <Text
            name={`${record.name}.articleNumberForOtherSpeciality`}
            disabled={disabled}
          />
        </TableCell>
      )}
      <TableCell>
        <Select
          disabled={disabled}
          plain
          clearable={false}
          name={`${record.name}.paidPer`}
          options={Object.values(PayUnit).toLabelValue(
            (v) => locale.contractsPrice.payUnit[v]
          )}
        />
      </TableCell>
      {showInputPeriod ? (
        <TableCell>
          <Select
            disabled={disabled}
            plain
            clearable={false}
            name={`${record.name}.period`}
            options={Object.values(PayPeriod).toLabelValue(
              (v) => locale.contractsPrice.payPeriod[v]
            )}
          />
        </TableCell>
      ) : (
        <TableCell>{locale.contractsPrice.payPeriod[record.period]}</TableCell>
      )}
      <TableCell>
        {showInputDays ? (
          <SelectCommon
            fullWidth
            disabled={disabled}
            value={formStore.getValue(`${record.name}.startDay`)}
            onChange={(v) => formStore.setValue(`${record.name}.startDay`, v)}
            name={`${record.name}.startDay`}
            groupBy="parent"
            sortGroups={false}
            optionType="checkbox"
            options={startDayOptions ?? dailyRecordOpt}
          />
        ) : (
          locale.contractsPrice.priceListDay[record.startDay]
        )}
      </TableCell>
      <TableCell>
        <Text name={`${record.name}.from`} disabled={disabled} mask="99:99" />
      </TableCell>
      <TableCell>
        <span style={{ padding: "0 5px", whiteSpace: "nowrap" }}> -- </span>
      </TableCell>
      <TableCell>
        {showInputDays ? (
          <SelectCommon
            fullWidth
            disabled={disabled}
            value={formStore.getValue(`${record.name}.endDay`)}
            onChange={(v) => formStore.setValue(`${record.name}.endDay`, v)}
            name={`${record.name}.endDay`}
            groupBy="parent"
            sortGroups={false}
            optionType="checkbox"
            options={endDayOptions ?? dailyRecordOpt}
          />
        ) : (
          locale.contractsPrice.priceListDay[record.endDay]
        )}
      </TableCell>
      <TableCell>
        <Text name={`${record.name}.to`} disabled={disabled} mask="99:99" />
      </TableCell>
      <TableCell>
        <Text
          name={`${record.name}.priceMultiplier`}
          disabled={disabled}
          TextFieldProps={{
            type: "number",
            inputProps: {
              style: { textAlign: "right" }
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Text
          name={`${record.name}.fixedPrice`}
          disabled={disabled}
          TextFieldProps={{
            type: "number",
            inputProps: { style: { textAlign: "right" } }
          }}
        />
      </TableCell>

      {isNationalProcurementPriceStrategy &&
        templateType === ContractsPriceTemplateType.ConsultantContract && (
          <>
            <TableCell>
              {getFixedPrice(
                tenant,
                formStore.getValue(`${record.name}.fixedPrice`),
                record,
                true,
                false
              )}
            </TableCell>
            <TableCell>
              {getFixedPrice(
                tenant,
                formStore.getValue(`${record.name}.fixedPrice`),
                record,
                true,
                true
              )}
            </TableCell>
          </>
        )}

      <TableCell align={"right"} className={classes.total}>
        <div className={classes.totalChild}>
          <FormControlLabel
            style={{ marginRight: 0 }}
            disabled={disabled}
            control={
              <Checkbox
                size={"small"}
                checked={
                  isCustom[record.index] ??
                  !!formStore.getValue(`${record.name}.customPrice`)
                }
                icon={<Edit />}
                checkedIcon={<Edit color={"primary"} />}
                onChange={(e) => {
                  handleShowCustomInput(e, record.index, `${record.name}.customPrice`);
                }}
              />
            }
            label=""
          />
          {isCustom[record.index] ? (
            <Text
              name={`${record.name}.customPrice`}
              disabled={disabled}
              TextFieldProps={{
                type: "number",
                fullWidth: false,
                inputProps: {
                  style: { textAlign: "right", width: 60 }
                }
              }}
            />
          ) : (
            (templateType === ContractsPriceTemplateType.ConsultantContract ||
              formStore.values.type === ContractsPriceTemplateType.ConsultantContract) &&
            getPrice(
              tenant,
              formStore.values.BasicPrice,
              record.priceMultiplier,
              record.fixedPrice,
              record
            )
          )}
        </div>
      </TableCell>

      {isNationalProcurementPriceStrategy &&
        templateType === ContractsPriceTemplateType.CustomerContract && (
          <TableCell>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Checkbox
                size={"small"}
                disabled={!store().user.can("customer-contract-second-pass:update:all")}
                checked={!!formStore.getValue(`${record.name}.isSecondPass`)}
                onChange={(e) =>
                  formStore.setValue(`${record.name}.isSecondPass`, e.target.checked)
                }
              />
            </div>
          </TableCell>
        )}

      {isNationalProcurementPriceStrategy &&
        templateType === ContractsPriceTemplateType.ConsultantContract && (
          <>
            <TableCell style={{ fontWeight: "bold", minWidth: 70 }}>
              {isCustom[record.index]
                ? getCustomPrice(
                    tenant,
                    formStore.getValue(`${record.name}.customPrice`),
                    record,
                    true,
                    false
                  )
                : (templateType === ContractsPriceTemplateType.ConsultantContract ||
                    formStore.values.type ===
                      ContractsPriceTemplateType.ConsultantContract) &&
                  getPrice(
                    tenant,
                    formStore.values.BasicPrice,
                    record.priceMultiplier,
                    record.fixedPrice,
                    record,
                    null,
                    true,
                    false
                  )}
            </TableCell>
            <TableCell style={{ fontWeight: "bold", minWidth: 70 }}>
              {isCustom[record.index]
                ? getCustomPrice(
                    tenant,
                    formStore.getValue(`${record.name}.customPrice`),
                    record,
                    true,
                    true
                  )
                : (templateType === ContractsPriceTemplateType.ConsultantContract ||
                    formStore.values.type ===
                      ContractsPriceTemplateType.ConsultantContract) &&
                  getPrice(
                    tenant,
                    formStore.values.BasicPrice,
                    record.priceMultiplier,
                    record.fixedPrice,
                    record,
                    null,
                    true,
                    true
                  )}
            </TableCell>
          </>
        )}
      <TableCell>
        <div className={classes.btnWrap}>
          {validationIcon}
          <IconButton size={"small"} disabled={disabled} onClick={handleDuplicate}>
            {<FileCopy />}
          </IconButton>
          <IconButton size={"small"} disabled={disabled} onClick={handleDelete}>
            {record.deleted ? <Restore /> : <Remove />}
          </IconButton>
          <IconButton size={"small"} data-movable-handle>
            <DragHandle />
          </IconButton>
        </div>
      </TableCell>
    </TableRow>
  );
};

const RowFullPeriod = ({
  record: x,
  validationResult,
  disabled,
  dragProps,
  formStore,
  templateType,
  handleDuplicate,
  handleDelete,
  isCustom,
  handleShowCustomInput,
  isNationalProcurementPriceStrategy,
  showArticleNumberForOtherSpeciality
}: RowProps) => {
  const { tenant } = useTenant();
  const theme = useTheme();
  const classes = useStyles(theme);
  const validationItem = validationResult.find((v) => v.contractOptionId === x?._id);
  const validationIcon = getValidationIcon(validationItem);
  return (
    <TableRow
      {...dragProps}
      key={x._id} // don't remove this to avoid holy crap
      className={classes.row}
    >
      <TableCell>
        <Text name={`${x.name}.articleNumber`} disabled={disabled} />
      </TableCell>
      {showArticleNumberForOtherSpeciality && (
        <TableCell>
          <Text name={`${x.name}.articleNumberForOtherSpeciality`} disabled={disabled} />
        </TableCell>
      )}
      <TableCell>
        <Select
          disabled={disabled}
          plain
          clearable={false}
          name={`${x.name}.paidPer`}
          options={Object.values(PayUnit).toLabelValue(
            (v) => locale.contractsPrice.payUnit[v]
          )}
        />
      </TableCell>
      <TableCell>{locale.contractsPrice.payPeriod[x.period]}</TableCell>
      <TableCell>
        <SelectCommon
          fullWidth
          disabled={disabled}
          value={formStore.getValue(`${x.name}.startDay`)}
          onChange={(v) => formStore.setValue(`${x.name}.startDay`, v)}
          name={`${x.name}.startDay`}
          groupBy="parent"
          sortGroups={false}
          optionType="checkbox"
          options={startDayOptFullPeriod}
        />
      </TableCell>

      <TableCell>
        <Text name={`${x.name}.from`} disabled={disabled} mask="99:99" />
      </TableCell>
      <TableCell>
        <span style={{ padding: "0 5px", whiteSpace: "nowrap" }}> -- </span>
      </TableCell>
      <TableCell>
        <SelectCommon
          fullWidth
          disabled={disabled}
          value={formStore.getValue(`${x.name}.endDay`)}
          onChange={(v) => formStore.setValue(`${x.name}.endDay`, v)}
          name={`${x.name}.endDay`}
          groupBy="parent"
          sortGroups={false}
          optionType="checkbox"
          options={endDayOptFullPeriod}
        />
      </TableCell>
      <TableCell>
        <Text name={`${x.name}.to`} disabled={disabled} mask="99:99" />
      </TableCell>
      <TableCell>
        <Text
          name={`${x.name}.priceMultiplier`}
          disabled={disabled}
          TextFieldProps={{
            type: "number",
            inputProps: {
              style: { textAlign: "right" }
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Text
          name={`${x.name}.fixedPrice`}
          disabled={disabled}
          TextFieldProps={{
            type: "number",
            inputProps: { style: { textAlign: "right" } }
          }}
        />
      </TableCell>

      {isNationalProcurementPriceStrategy &&
        templateType === ContractsPriceTemplateType.ConsultantContract && (
          <>
            <TableCell>
              {getFixedPrice(
                tenant,
                formStore.getValue(`${x.name}.fixedPrice`),
                x,
                true,
                false
              )}
            </TableCell>
            <TableCell>
              {getFixedPrice(
                tenant,
                formStore.getValue(`${x.name}.fixedPrice`),
                x,
                true,
                true
              )}
            </TableCell>
          </>
        )}

      <TableCell align={"right"} className={classes.total}>
        <div className={classes.totalChild}>
          <FormControlLabel
            style={{ marginRight: 0 }}
            disabled={disabled}
            control={
              <Checkbox
                size={"small"}
                checked={
                  isCustom[x.index] ?? !!formStore.getValue(`${x.name}.customPrice`)
                }
                icon={<Edit />}
                checkedIcon={<Edit color={"primary"} />}
                onChange={(e) => {
                  handleShowCustomInput(e, x.index, `${x.name}.customPrice`);
                }}
              />
            }
            label=""
          />
          {isCustom[x.index] ? (
            <Text
              name={`${x.name}.customPrice`}
              disabled={disabled}
              TextFieldProps={{
                type: "number",
                fullWidth: false,
                inputProps: {
                  style: { textAlign: "right", width: 60 }
                }
              }}
            />
          ) : (
            (templateType === ContractsPriceTemplateType.ConsultantContract ||
              formStore.values.type === ContractsPriceTemplateType.ConsultantContract) &&
            getPrice(
              tenant,
              formStore.values.BasicPrice,
              x.priceMultiplier,
              x.fixedPrice,
              x
            )
          )}
        </div>
      </TableCell>

      {isNationalProcurementPriceStrategy &&
        templateType === ContractsPriceTemplateType.CustomerContract && (
          <TableCell>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Checkbox
                size={"small"}
                disabled={!store().user.can("customer-contract-second-pass:update:all")}
                checked={!!formStore.getValue(`${x.name}.isSecondPass`)}
                onChange={(e) =>
                  formStore.setValue(`${x.name}.isSecondPass`, e.target.checked)
                }
              />
            </div>
          </TableCell>
        )}

      {isNationalProcurementPriceStrategy &&
        templateType === ContractsPriceTemplateType.ConsultantContract && (
          <>
            <TableCell style={{ fontWeight: "bold", minWidth: 70 }}>
              {isCustom[x.index]
                ? getCustomPrice(
                    tenant,
                    formStore.getValue(`${x.name}.customPrice`),
                    x,
                    true,
                    false
                  )
                : (templateType === ContractsPriceTemplateType.ConsultantContract ||
                    formStore.values.type ===
                      ContractsPriceTemplateType.ConsultantContract) &&
                  getPrice(
                    tenant,
                    formStore.values.BasicPrice,
                    x.priceMultiplier,
                    x.fixedPrice,
                    x,
                    null,
                    true,
                    false
                  )}
            </TableCell>
            <TableCell style={{ fontWeight: "bold", minWidth: 70 }}>
              {isCustom[x.index]
                ? getCustomPrice(
                    tenant,
                    formStore.getValue(`${x.name}.customPrice`),
                    x,
                    true,
                    true
                  )
                : (templateType === ContractsPriceTemplateType.ConsultantContract ||
                    formStore.values.type ===
                      ContractsPriceTemplateType.ConsultantContract) &&
                  getPrice(
                    tenant,
                    formStore.values.BasicPrice,
                    x.priceMultiplier,
                    x.fixedPrice,
                    x,
                    null,
                    true,
                    true
                  )}
            </TableCell>
          </>
        )}

      <TableCell>
        <div className={classes.btnWrap}>
          {validationIcon}
          <IconButton size={"small"} disabled={disabled} onClick={handleDuplicate}>
            {<FileCopy />}
          </IconButton>
          <IconButton size={"small"} disabled={disabled} onClick={handleDelete}>
            {x.deleted ? <Restore /> : <Remove />}
          </IconButton>
          <IconButton size={"small"} data-movable-handle>
            <DragHandle />
          </IconButton>
        </div>
      </TableCell>
    </TableRow>
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  leftContainer: {
    flex: 1,
    marginLeft: 30,
    marginRight: 20
  },
  subTitle: {
    textAlign: "center",
    backgroundColor: "#eee",
    padding: "3px 0"
  },
  row: {
    verticalAlign: "bottom",

    "& td": {
      borderBottom: 0,
      padding: "0 2px"
    }
  },
  btnWrap: {
    display: "flex",
    alignItems: "center"
  },
  total: {
    // paddingBottom: 7,
    fontWeight: "bold",
    minWidth: 120
  },
  totalChild: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    whiteSpace: "nowrap"
  },
  timeAdornment: {
    fontSize: 15,
    cursor: "pointer"
  }
}));
